import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { forgotPassword } from "../../../Redux/Actions/AuthActions/ForgotPasswordAction";
import { useAppThunkDispatch } from "../../../Redux/hooks";
import AuthActionBtn from "../../Shared/AuthActionBtn/AuthActionBtn";

type Props = {
  email: string;
  onVerified: (otp: string) => void;
  onBack: () => void;
};

const OTP_LENGTH = 6;

const ForgotPasswordOtpStep = ({ email, onVerified, onBack }: Props) => {
  const dispatch = useAppThunkDispatch();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [isResending, setIsResending] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const timer = setInterval(() => {
      setRemainingSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [remainingSeconds]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((digit, i) => {
      next[i] = digit;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) {
      toast.error("Please enter the 6-digit code from your email.");
      return;
    }
    onVerified(code);
  };

  const handleResend = async () => {
    if (remainingSeconds > 0 || isResending) return;
    setIsResending(true);
    const result = await dispatch(forgotPassword({ email }));
    setIsResending(false);
    if (result.success) {
      toast.success("A new code has been sent to your email.");
      setOtp(Array(OTP_LENGTH).fill(""));
      setRemainingSeconds(60);
      inputRefs.current[0]?.focus();
    } else {
      toast.error(result.message || "Failed to resend code.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="ForgotPasswordBox forgotPasswordStep">
      <p className="forgotPasswordStepTitle">Verify your email</p>
      <p className="forgotPasswordStepHint">
        Enter the 6-digit code sent to <strong>{email}</strong>
      </p>

      <div className="otpInputRow" onPaste={handlePaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="otpDigitInput"
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      <p className="forgotPasswordResend">
        {remainingSeconds > 0 ? (
          <>Resend code in {formatTime(remainingSeconds)}</>
        ) : (
          <button
            type="button"
            className="forgotPasswordResendBtn"
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? "Sending..." : "Resend code"}
          </button>
        )}
      </p>

      <AuthActionBtn
        isFetching={false}
        buttonLabel="Continue"
        hideFooterLink
      />
      <span className="forgotPasswordBackLink">
        <button type="button" className="forgotPasswordLinkBtn" onClick={onBack}>
          Change email
        </button>
      </span>
    </form>
  );
};

export default ForgotPasswordOtpStep;
